/**
 * Part reference data (T12, frontend-only). Single source for the Inventory
 * Add/Edit Part form selects (condition / side / position) and any read-only
 * part chips, mirroring the backend `Part` choices until a server-driven source
 * replaces it. Pages import these instead of inlining option arrays.
 *
 * `CONDITIONS`, `SIDES`, `POSITIONS` entries are select-option order. `condition`
 * and `side` send a stable enum key (the model exposes choices); `position` is a
 * free-text CharField on the model, so its options are a bounded list of common
 * placements (DESIGN.md control taxonomy) whose VALUE is the raw string sent to
 * the backend — `value === label` on purpose.
 *
 * The matching `*_LABEL` maps resolve a stored key to its display name and the
 * `*_OPTIONS` arrays are the `[{ value, label }]` shape the corelib `SelectField`
 * `options` prop expects.
 */

/** Ordered condition options as `[key, label]` (enum key → display name). */
const CONDITIONS = [
  ['new', 'New'],
  ['used_excellent', 'Used – Excellent'],
  ['used_good', 'Used – Good'],
  ['used_fair', 'Used – Fair'],
  ['for_parts', 'For Parts'],
];

/** Ordered side options as `[key, label]` (enum key → display name). */
const SIDES = [
  ['left', 'Left'],
  ['right', 'Right'],
  ['front', 'Front'],
  ['rear', 'Rear'],
];

/**
 * Common placement options for the free-text `position` CharField. Bounded
 * Select of common placements; the VALUE is the raw string sent to the backend
 * (value === label), matching the original inline `POSITIONS` array.
 */
const POSITIONS = [
  'Front Left',
  'Front Right',
  'Rear Left',
  'Rear Right',
  'Front',
  'Rear',
  'Upper',
  'Lower',
  'Inner',
  'Outer',
];

/** Condition key → display label. */
const CONDITION_LABEL = Object.fromEntries(CONDITIONS.map(([key, label]) => [key, label]));

/** Side key → display label. */
const SIDE_LABEL = Object.fromEntries(SIDES.map(([key, label]) => [key, label]));

/** Condition `SelectField` options (`[{ value, label }]`). */
const CONDITION_OPTIONS = CONDITIONS.map(([value, label]) => ({ value, label }));

/** Side `SelectField` options (`[{ value, label }]`). */
const SIDE_OPTIONS = SIDES.map(([value, label]) => ({ value, label }));

/** Position `SelectField` options — value === label (free-text CharField). */
const POSITION_OPTIONS = POSITIONS.map((value) => ({ value, label: value }));

export {
  CONDITION_LABEL,
  CONDITION_OPTIONS,
  CONDITIONS,
  POSITION_OPTIONS,
  POSITIONS,
  SIDE_LABEL,
  SIDE_OPTIONS,
  SIDES,
};
